/**
 * Centralized status badge mapping for CMS display only.
 * UI layer — does not affect business logic, APIs, or stored values.
 */

export type StatusBadgeTone =
  | 'success'
  | 'inactive'
  | 'warning'
  | 'danger'
  | 'info'
  | 'orange'
  | 'slate'
  | 'neutral';

export type StatusBadgeColors = {
  bg: string;
  text: string;
  border: string;
};

export const STATUS_TONE_COLORS: Record<StatusBadgeTone, StatusBadgeColors> = {
  success: {
    bg: 'var(--tc-success-soft)',
    text: 'var(--tc-success)',
    border: 'var(--tc-success-border)',
  },
  inactive: {
    bg: 'var(--tc-bg-muted)',
    text: 'var(--tc-text-muted)',
    border: 'var(--tc-border)',
  },
  warning: {
    bg: 'var(--tc-warning-soft)',
    text: 'var(--tc-warning)',
    border: 'var(--tc-warning-border)',
  },
  danger: {
    bg: 'var(--tc-danger-soft)',
    text: 'var(--tc-danger)',
    border: 'var(--tc-danger-border)',
  },
  info: {
    bg: 'var(--tc-info-soft)',
    text: 'var(--tc-info)',
    border: 'var(--tc-info-border)',
  },
  orange: {
    bg: 'var(--tc-orange-soft)',
    text: 'var(--tc-orange)',
    border: 'var(--tc-orange-border)',
  },
  slate: {
    bg: 'var(--tc-slate-soft)',
    text: 'var(--tc-slate)',
    border: 'var(--tc-slate-border)',
  },
  neutral: {
    bg: 'var(--tc-bg-muted)',
    text: 'var(--tc-text)',
    border: 'var(--tc-border)',
  },
};

/**
 * Explicit tone map for every known CMS status token.
 * Unknown tokens fall back to `neutral` in resolveStatusTone().
 */
export const STATUS_TONE_MAP: Record<string, StatusBadgeTone> = {
  // Green — success
  active: 'success',
  paid: 'success',
  refunded: 'orange',
  completed: 'success',
  succeeded: 'success',
  restored: 'success',
  payment_received: 'success',
  invoice_generated: 'success',
  success: 'success',
  live: 'success',
  enabled: 'success',
  operational: 'success',
  online: 'success',
  billable: 'success',
  verified: 'success',

  // Gray — inactive
  inactive: 'inactive',
  disabled: 'inactive',
  offline: 'inactive',
  paused: 'inactive',
  non_billable: 'inactive',

  // Yellow — warning
  pending: 'warning',
  grace_period: 'warning',
  cancellation_pending: 'warning',
  incomplete: 'warning',
  warning: 'warning',
  archived: 'warning',

  // Blue — info
  processing: 'info',
  trialing: 'info',
  primary: 'info',
  info: 'info',

  // Red — danger
  unpaid: 'danger',
  failed: 'danger',
  failure: 'danger',
  error: 'danger',
  danger: 'danger',
  critical: 'danger',
  cancelled: 'danger',
  canceled: 'danger',
  overdue: 'danger',
  removed: 'danger',
  incomplete_expired: 'danger',
  unregistered: 'danger',

  // Orange
  suspended: 'orange',
  expired: 'orange',
  past_due: 'orange',

  // Slate
  draft: 'slate',

  // Neutral
  unknown: 'neutral',
  default: 'neutral',
};

const STATUS_LABEL_OVERRIDES: Record<string, string> = {
  active: 'Active',
  inactive: 'Inactive',
  paid: 'Paid',
  refunded: 'Refunded',
  unpaid: 'Unpaid',
  pending: 'Pending',
  processing: 'Processing',
  completed: 'Completed',
  failed: 'Failed',
  cancelled: 'Cancelled',
  canceled: 'Cancelled',
  expired: 'Expired',
  suspended: 'Suspended',
  draft: 'Draft',
  overdue: 'Overdue',
  succeeded: 'Succeeded',
  success: 'Success',
  restored: 'Restored',
  payment_received: 'Payment Received',
  invoice_generated: 'Invoice Generated',
  grace_period: 'Grace Period',
  cancellation_pending: 'Cancellation Pending',
  removed: 'Removed',
  online: 'Online',
  offline: 'Offline',
  archived: 'Archived',
  live: 'Live',
  enabled: 'Enabled',
  disabled: 'Disabled',
  operational: 'Operational',
  paused: 'Paused',
  trialing: 'Trialing',
  past_due: 'Past Due',
  incomplete: 'Incomplete',
  incomplete_expired: 'Incomplete Expired',
  unknown: 'Unknown',
  billable: 'Billable',
  non_billable: 'Non-Billable',
  unregistered: 'Unregistered',
  verified: 'Verified',
};

export function normalizeStatusToken(value: string | null | undefined): string {
  if (typeof value !== 'string') return '';
  return value.trim().toLowerCase().replace(/[\s-]+/g, '_');
}

/** Human-readable label; display formatting only — never mutates stored values. */
export function formatStatusLabel(
  status: string | null | undefined,
  label?: string | null
): string {
  if (label?.trim()) return label.trim();
  const normalized = normalizeStatusToken(status);
  if (!normalized) return 'Unknown';
  if (STATUS_LABEL_OVERRIDES[normalized]) return STATUS_LABEL_OVERRIDES[normalized];
  return normalized
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

/** Maps any raw status string to a semantic color tone. */
export function resolveStatusTone(status: string | null | undefined): StatusBadgeTone {
  const normalized = normalizeStatusToken(status);
  if (!normalized) return 'neutral';
  return STATUS_TONE_MAP[normalized] ?? 'neutral';
}

export function getStatusBadgeColors(status: string | null | undefined): StatusBadgeColors {
  return STATUS_TONE_COLORS[resolveStatusTone(status)];
}

export function resolveDeviceStatusBadge(device: {
  active?: boolean | null;
  removal_date?: string | null;
}): { status: string; label: string } {
  if (device.active && !device.removal_date) {
    return { status: 'active', label: 'Active' };
  }
  if (device.removal_date) {
    return { status: 'removed', label: 'Removed' };
  }
  return { status: 'inactive', label: 'Inactive' };
}

export function resolveProjectActiveBadge(isActive: boolean): { status: string; label: string } {
  return isActive
    ? { status: 'active', label: 'Active' }
    : { status: 'inactive', label: 'Inactive' };
}

/** Full device lifecycle badges for list rows (display only). */
export function resolveDeviceLastEventBadge(
  eventType: string
): { status: string; label: string } | null {
  switch (eventType) {
    case 'PUSH_DISABLED':
      return { status: 'warning', label: 'Push Disabled' };
    case 'PUSH_RESTORED':
      return { status: 'active', label: 'Push Restored' };
    case 'REMOVAL_SCHEDULED':
      return { status: 'pending', label: 'Removal Scheduled' };
    case 'REMOVAL_CANCELLED':
      return { status: 'restored', label: 'Removal Cancelled' };
    case 'VIEW_DEVICE':
      return { status: 'processing', label: 'Inspected' };
    default:
      return null;
  }
}

export function resolveCompanyDeviceBadges(device: {
  active?: boolean | null;
  removal_date?: string | null;
  isBillable?: boolean;
  isRemovalScheduled?: boolean;
  isPushDisabledTemporary?: boolean;
}): Array<{ status: string; label: string }> {
  const lifecycle = resolveDeviceStatusBadge(device);
  const push = resolveDevicePushStatusBadge({
    isRemovalScheduled: device.isRemovalScheduled === true,
    isPushDisabledTemporary: device.isPushDisabledTemporary === true,
  });
  const billable = resolveDeviceBillableBadge(device.isBillable !== false);
  return [lifecycle, push, billable];
}

export function resolveDevicePushStatusBadge(params: {
  isRemovalScheduled: boolean;
  isPushDisabledTemporary: boolean;
}): { status: string; label: string } {
  if (params.isRemovalScheduled) {
    return { status: 'pending', label: 'Pending Deletion' };
  }
  if (params.isPushDisabledTemporary) {
    return { status: 'warning', label: 'Push Disabled (Temporary)' };
  }
  return { status: 'active', label: 'Push Active' };
}

export function resolveDeviceBillableBadge(isBillable: boolean): { status: string; label: string } {
  return isBillable
    ? { status: 'billable', label: 'Billable Seat' }
    : { status: 'non_billable', label: 'Non-Billable' };
}

export function resolveDeviceOnlineBadge(isOnline: boolean): { status: string; label: string } {
  return isOnline
    ? { status: 'online', label: 'Online' }
    : { status: 'offline', label: 'Offline' };
}

export function resolveSettlementStatusBadge(
  paidDate: string | null | undefined,
  invoiceStatus?: string | null
): {
  status: string;
  label: string;
} {
  if (invoiceStatus === 'refunded') {
    return { status: 'refunded', label: 'Refunded' };
  }
  if (invoiceStatus === 'paid' || paidDate) {
    return { status: 'paid', label: 'Paid' };
  }
  return { status: 'pending', label: 'Pending' };
}
